import React, { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Divider,
  Modal,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import PredictionCheckPopup from "./PredictionCheckPopup";
import GA4 from "../../../components/GoogleAnalytics4/GA-4";
import CloseIcon from '@mui/icons-material/Close';

const GradientCard = styled(Card)(({ theme }) => ({
  background: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.15)',
  }
}));

const ResultAccordion = styled(Accordion)(({ theme }) => ({
  background: 'white',
  borderRadius: '12px !important',
  marginBottom: '1rem',
  border: '1px solid rgba(92, 167, 201, 0.1)',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    borderRadius: '12px',
    '&:hover': {
      background: 'rgba(191, 226, 186, 0.05)',
    }
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #bfe2ba, #5ca7c9)',
  color: 'white',
  fontWeight: 600,
  padding: '20px 10px',
  '& .MuiChip-label': {
    fontSize: '1rem',
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '100%',
  borderRadius: '20px',
  overflow: 'hidden',
  background: 'linear-gradient(145deg, #ffffff, #f3f3f3)',
  boxShadow: '20px 20px 60px #d9d9d9, -20px -20px 60px #ffffff',
  border: '4px solid white',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '16px',
    padding: '4px',
    background: 'linear-gradient(135deg, #bfe2ba, #5ca7c9)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    zIndex: 1
  },
  '&:hover': {
    transform: 'scale(1.02)',
    transition: 'transform 0.3s ease-in-out',
    '& .image-overlay': {
      opacity: 1
    }
  }
}));

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 50%)',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '20px',
  zIndex: 2
}));

const StyledImage = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)'
  }
});

const ModalImage = styled('img')({
  maxWidth: '90vw',
  maxHeight: '90vh',
  objectFit: 'contain',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
});

export default function PredictionResult({ image }) {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const xsmall = useMediaQuery(theme.breakpoints.down("xs"));
  const medium = useMediaQuery(theme.breakpoints.up("md"));
  
  const [crop, setCrop] = useState("");
  const [disease, setDisease] = useState("");
  const [cause, setCause] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [mgmt, setMgmt] = useState("");
  const [file_name, setFileName] = useState("");
  const [diseasedImg, setDiseasedImg] = useState(null);
  const [percentage, setPercentage] = useState(null);
  const [predictionCheckModal, setPredictionCheckModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const result = location?.state?.result;

  useEffect(() => {
    if (result) {
      setCrop(result.crop);
      setDisease(result.disease);
      setPercentage(result.percentage);
      setFileName(result.file_name);
      setDiseasedImg(location.state.diseasedImg);
      setPredictionCheckModal(result.status === 'True' ? false : true);
      setCause(result.cause);
      setSymptoms(result.symptom);
      setMgmt(result.mgmt);

      let ga4Data = {
        user: user,
        prediction_result: result
      };
      GA4.diseasePrediction(ga4Data);
    }
  }, [result]);

  const handleImageClick = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
        textAlign: 'center',
        mb: 6,
        background: 'linear-gradient(170deg, rgba(191, 226, 186, 0.2), rgba(92,167,201, 0.2))',
        py: 4,
        borderRadius: '20px'
      }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.75rem", md: "2.5rem" },
            color: "#2c5282",
            mb: 2
          }}
        >
          {t("result")}
        </Typography>
      </Box>

      <GradientCard>
        <CardContent>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <ImageContainer onClick={handleImageClick}>
                <StyledImage
                  src={image}
                  alt="diseased_crop"
                />
                <ImageOverlay className="image-overlay">
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      textAlign: 'center',
                      fontWeight: 500,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    {t("clickToEnlarge")}
                  </Typography>
                </ImageOverlay>
              </ImageContainer>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("cropname")}
                  </Typography>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    {crop}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("diseaseName")}
                  </Typography>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    {disease}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* <StyledChip
                      label={`${Math.round(percentage)}% ${t("accuracy")}`}
                    /> */}
                    <Box sx={{ width: 80, height: 80 }}>
                      <CircularProgressbarWithChildren
                        value={Math.round(percentage)}
                        styles={buildStyles({
                          rotation: 0.25,
                          strokeLinecap: "butt",
                          pathColor: `url(#gradient)`,
                          trailColor: "rgba(0,0,0,0.1)",
                        })}
                      >
                        <svg style={{ height: 0 }}>
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#bfe2ba" />
                              <stop offset="100%" stopColor="#5ca7c9" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <Typography variant="caption" fontWeight="600">
                          {Math.round(percentage)}%
                        </Typography>
                      </CircularProgressbarWithChildren>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </GradientCard>

      <Box sx={{ mt: 4 }}>
        <ResultAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="600">
              {t("causeDisease")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ pl: 2 }}>
              {cause && cause.map((des, index) => (
                des.length > 0 && (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      mb: 1,
                      color: 'text.secondary',
                      position: 'relative',
                      pl: 3,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #bfe2ba, #5ca7c9)',
                      }
                    }}
                  >
                    {des}
                  </Typography>
                )
              ))}
            </Box>
          </AccordionDetails>
        </ResultAccordion>

        <ResultAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="600">
              {t("symptom")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ pl: 2 }}>
              {symptoms && symptoms.map((des, index) => (
                des.length > 0 && (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      mb: 1,
                      color: 'text.secondary',
                      position: 'relative',
                      pl: 3,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #bfe2ba, #5ca7c9)',
                      }
                    }}
                  >
                    {des}
                  </Typography>
                )
              ))}
            </Box>
          </AccordionDetails>
        </ResultAccordion>

        <ResultAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="600">
              {t("management")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ pl: 2 }}>
              {mgmt && mgmt.map((des, index) => (
                des.length > 0 && (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      mb: 1,
                      color: 'text.secondary',
                      position: 'relative',
                      pl: 3,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #bfe2ba, #5ca7c9)',
                      }
                    }}
                  >
                    {des}
                  </Typography>
                )
              ))}
            </Box>
          </AccordionDetails>
        </ResultAccordion>
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="image-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: 'rgba(0, 0, 0, 0.8)'
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: -20,
              top: -20,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <ModalImage src={image} alt="diseased_crop" />
        </Box>
      </Modal>

      {predictionCheckModal && (
        <PredictionCheckPopup
          t={t}
          show={predictionCheckModal}
          onHide={() => setPredictionCheckModal(false)}
          user_id={user && user.id}
          file_name={file_name}
          crop={crop}
          setPredictionCheckModal={setPredictionCheckModal}
        />
      )}
    </Container>
  );
}
